import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FeatherIcon from 'feather-icons-react';
import SVG_BANKS from "src/assets/svg/svg_banks";
import { formatMoneyWithCurrency } from 'src/utils/formatter';
import { banksFormat } from 'src/utils/bankList';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import moment from 'moment';
import { generateKey } from 'src/utils/generateKey';
import { TransactionType } from 'src/utils/enum';
import { isFullyReconciled } from 'src/utils/reconciliationProgress';
import { dt } from 'src/utils/documentsI18n';
import { importAlertState } from 'src/redux/actions/alert';
import { RecordMovementShareLink } from 'src/graphql/conciliacion';

const buildMovementShareUrl = ({ transactionId, companyId, location }) => {
  const params = new URLSearchParams();
  params.set('type', 'ALL');
  params.set('page', '1');
  params.set('offset', '20');
  params.set('transactionId', String(transactionId));
  if (companyId) params.set('companyId', String(companyId));

  // Preserve comment/accounting tab when sharing from that view
  const current = new URLSearchParams(location.search || '');
  if (current.get('isComment') === 'true') params.set('isComment', 'true');
  if (current.get('isAccounting') === 'true') params.set('isAccounting', 'true');
  if (current.get('isFiles') === 'true') params.set('isFiles', 'true');

  const path = location.pathname?.includes('conciliacion')
    ? location.pathname
    : '/conciliacion';
  return `${window.location.origin}${path}?${params.toString()}`;
};

const TransactionInfoBar = ({ editData }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { selectedCompany } = useSelector((state) => state.companyList);
  const { executeMutation: recordMovementShareLink } = RecordMovementShareLink();
  const hideBank = location.pathname.includes('documentos_tributarios');

  const transactionId = editData?.id || editData?._id;
  const companyId = selectedCompany?.id || editData?.companyId;

  const handleCopyShareLink = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!transactionId) return;

    const url = buildMovementShareUrl({ transactionId, companyId, location });
    try {
      await navigator.clipboard.writeText(url);
      dispatch(
        importAlertState({
          content: dt('reconcile.linkCopied'),
          state: 'success',
          duration: 3,
        })
      );
      if (companyId) {
        void recordMovementShareLink({
          companyId: String(companyId),
          transactionId: String(transactionId),
          url,
        }).catch((auditErr) => {
          console.warn('No se pudo registrar auditoría de share de movimiento:', auditErr);
        });
      }
    } catch {
      dispatch(
        importAlertState({
          content: dt('reconcile.linkCopyError'),
          state: 'error',
          duration: 4,
        })
      );
    }
  }, [transactionId, companyId, location, dispatch, recordMovementShareLink]);

  if (!editData) {
    return null;
  }

  const amount = editData.amount || editData.meta?.amount || 0;
  const currency = editData.currency || editData.meta?.currency || 'CLP';
  const isIncome = editData.type === 'RECIEVED' || editData.meta?.isReceived === false;
  const bankId = editData.bankId || editData.meta?.bankId;
  
  // Calculate remaining amount - check both direct fields and meta fields
  const remainingRaw = editData.remaining !== undefined
    ? editData.remaining
    : (editData.meta?.remaining !== undefined ? editData.meta?.remaining : amount);
  const remainingAbs = Math.abs(Number(remainingRaw ?? 0));
  const matchs = editData.matchs || editData.meta?.matchs?.inMatch;
  const fullyReconciled = isFullyReconciled(amount, remainingRaw, matchs);
  
  // Build description the same way as transactionRow.jsx
  // Handle both transactions and documents
  const transactionDescription = editData.description || editData.meta?.description || '';
  const isOut = editData.type === TransactionType.SPEND;
  
  // Get receiver/issuer info - check both direct fields and meta fields
  const receiver = editData.receiver || editData.meta?.receiverId;
  const receiverName = editData.receiverName || editData.meta?.receiverName;
  const issuer = editData.issuer || editData.meta?.issuerId;
  const issuerName = editData.issuerName || editData.meta?.issuerName;
  
  const name = isOut
    ? receiver ? (`${receiverName ? generateKey(transactionDescription).includes(generateKey(receiverName)) ? '' : receiverName : ''} RUT: ${receiver}`) : ''
    : issuer ? `${issuerName ? generateKey(transactionDescription).includes(generateKey(issuerName)) ? '' : issuerName : ''} RUT: ${issuer}` : '';
  const description = `${transactionDescription} ${name ? '::' : ''} ${name ? name : ''}`.trim();

  return (
    <>
      <div className="tw-w-full tw-bg-gray-50 tw-rounded-lg tw-p-6 tw-mb-4 tw-shadow-sm">
        <div className="tw-flex tw-items-start tw-gap-4">
          {/* Bank Logo */}
          {bankId && (
            <div className="tw-flex-shrink-0 tw-mt-1">
              <img 
                src={SVG_BANKS(bankId)} 
                alt={banksFormat[bankId] || 'Bank'} 
                className="tw-w-10 tw-h-10"
                style={{ objectFit: 'contain' }}
                title={banksFormat[bankId]}
              />
            </div>
          )}
          
          {/* Main Content */}
          <div className="tw-flex-1 tw-min-w-0">
            {/* Amount and Date Row */}
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-mb-2">
              <div className="tw-flex tw-items-center tw-gap-3">
                <span className={`tw-text-lg tw-font-semibold ${isIncome ? 'tw-text-green-600' : 'tw-text-red-600'}`}>
                  {isIncome ? '↑' : '↓'} {formatMoneyWithCurrency(amount, currency)}
                </span>
                {(editData.stringDate || editData.documentDate) && (
                  <span className="tw-text-xs tw-text-gray-500 tw-align-middle">
                    {editData.stringDate 
                      ? moment(editData.stringDate, 'DD/MM/YYYY').format('DD/MM/YYYY')
                      : moment(editData.documentDate).format('DD/MM/YYYY')}
                  </span>
                )}
              </div>
              
              {/* Remaining Amount Badge + share link */}
              <div className="tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0">
                <span className={`tw-inline-flex tw-items-center tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-semibold tw-whitespace-nowrap ${
                  fullyReconciled
                    ? 'tw-bg-green-100 tw-text-green-700'
                    : 'tw-bg-blue-100 tw-text-blue-700'
                }`}>
                  {fullyReconciled
                    ? `✓ ${dt('reconcile.reconciled')}`
                    : dt('reconcile.remaining', {
                        amount: formatMoneyWithCurrency(remainingAbs, currency),
                      })}
                </span>
                {transactionId && (
                  <button
                    type="button"
                    onClick={handleCopyShareLink}
                    title={dt('reconcile.shareMovementLink')}
                    aria-label={dt('reconcile.shareMovementLink')}
                    className="tw-inline-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-full tw-border-0 tw-bg-transparent tw-text-gray-500 hover:tw-text-[#176f80] hover:tw-bg-gray-200 tw-cursor-pointer tw-transition-colors"
                  >
                    <FeatherIcon icon="share-2" size={15} />
                  </button>
                )}
              </div>
            </div>

            {/* Bank and Account Info */}
            {!hideBank && bankId && (
              <div className="tw-text-sm tw-text-gray-600 tw-mb-1 tw-font-medium">
                {banksFormat[bankId]} • {dt('reconcile.account')} {editData.accountNumber || '-'}
              </div>
            )}

            {/* Description */}
            {description && (
              <div className="tw-text-sm tw-text-gray-700 tw-leading-relaxed">
                {description}
              </div>
            )}
            
            {/* Comments as separate line if exists */}
            {(editData.comments || editData.meta?.comments) && (
              <div className="tw-text-xs tw-text-gray-500 tw-mt-1 tw-italic">
                {editData.comments || editData.meta?.comments}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TransactionInfoBar;
